import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import siteConfig from "../../../site/config/site";

import {
  getRoutePath,
} from "../../../site/config/routes";

import SocialLinks from "../common/SocialLinks";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  FOOTER
  ============================================================

  Asiakaskohtaiset tiedot tulevat siteConfigista.

  Footer tukee:
  - brändiä
  - navigaatiota
  - sähköpostia
  - puhelinta
  - studiota
  - studion osoitetta
  - Google Maps -linkkiä
  - yrityksen virallista nimeä
  - Y-tunnusta
  - somekanavia

  Tyhjiä tietoja ei renderöidä.
*/

export default function Footer({
  language = "fi",
}) {
  const {
    t,
  } = useTranslation(
    "common"
  );

  const year =
    new Date().getFullYear();

  const contact =
    siteConfig.contact ||
    {};

  const studio =
    contact.studio ||
    {};

  const company =
    contact.company ||
    {};

  const labels =
    language === "en"
      ? {
          navigation:
            "Navigation",

          contact:
            "Contact",

          studio:
            "Studio",

          company:
            "Company",

          businessId:
            "Business ID",

          openMap:
            "Open in Maps",
        }
      : {
          navigation:
            "Navigaatio",

          contact:
            "Yhteystiedot",

          studio:
            "Studio",

          company:
            "Yritys",

          businessId:
            "Y-tunnus",

          openMap:
            "Avaa kartassa",
        };

  /*
    ============================================================
    NAVIGATION
    ============================================================
  */

  const links = [
    {
      route: "home",
      label:
        t(
          "pages.home"
        ),
    },

    {
      route:
        "portfolio",

      label:
        t(
          "navigation.portfolio"
        ),
    },

    {
      route:
        "services",

      label:
        t(
          "navigation.services"
        ),
    },

    {
      route:
        "studio",

      label:
        labels.studio,
    },

    {
      route:
        "pricing",

      label:
        t(
          "navigation.pricing"
        ),
    },

    {
      route:
        "about",

      label:
        t(
          "navigation.about"
        ),
    },

    {
      route:
        "contact",

      label:
        t(
          "navigation.contact"
        ),
    },
  ];

  /*
    ============================================================
    CONTACT DATA
    ============================================================
  */

  const email =
    String(
      contact.email ||
      ""
    ).trim();

  const phone =
    String(
      contact.phone ||
      ""
    ).trim();

  const phoneHref =
    phone
      ? `tel:${phone.replace(
          /[^\d+]/g,
          ""
        )}`
      : "";

  /*
    ============================================================
    STUDIO ADDRESS
    ============================================================
  */

  const studioName =
    String(
      studio.name ||
      ""
    ).trim();

  const studioStreet =
    String(
      studio.street ||
      ""
    ).trim();

  const studioPostalCode =
    String(
      studio.postalCode ||
      ""
    ).trim();

  const studioCity =
    String(
      studio.city ||
      ""
    ).trim();

  const studioCountry =
    String(
      studio.country ||
      ""
    ).trim();

  const studioMapsUrl =
    String(
      studio.mapsUrl ||
      ""
    ).trim();

  const studioCityLine =
    [
      studioPostalCode,
      studioCity,
    ]
      .filter(Boolean)
      .join(" ");

  const hasStudio =
    Boolean(
      studioName ||
      studioStreet ||
      studioPostalCode ||
      studioCity
    );

  /*
    ============================================================
    COMPANY
    ============================================================
  */

  const legalName =
    String(
      company.legalName ||
      ""
    ).trim();

  const businessId =
    String(
      company.businessId ||
      ""
    ).trim();

  const hasCompany =
    Boolean(
      legalName ||
      businessId
    );

  const hasContact =
    Boolean(
      email ||
      phone
    );

  const hasSocials =
    Boolean(
      Object.values(
        siteConfig.socials ||
        {}
      ).some(Boolean)
    );

  /*
    ============================================================
    RENDER
    ============================================================
  */

  return (
    <footer
      className="
        border-t
        border-border
        bg-canvas
        text-text
      "
    >
      <div
        className="
          mx-auto
          w-full
          max-w-editorial
          px-6
          py-14

          sm:px-10
          sm:py-18

          lg:px-16
          lg:py-20
        "
      >
        {/* ====================================================
            MAIN GRID
            ==================================================== */}

        <div
          className="
            grid
            gap-x-12
            gap-y-12

            sm:grid-cols-2

            lg:grid-cols-[1.35fr_0.85fr_0.9fr_1fr]
            lg:gap-x-14
          "
        >
          {/* ==================================================
              BRAND
              ================================================== */}

          <div>
            <p
              className="
                m-0
                max-w-sm
                font-display
                text-3xl
                tracking-[-0.035em]

                sm:text-4xl
              "
            >
              {
                siteConfig.brand
                  .name
              }
            </p>

            <p
              className="
                mb-0
                mt-4
                max-w-xs
                text-xs
                leading-6
                text-muted
              "
            >
              {t(
                "footer.tagline"
              )}
            </p>

            {hasSocials && (
              <div
                className="
                  mt-7
                "
              >
                <SocialLinks
                  compact
                />
              </div>
            )}
          </div>

          {/* ==================================================
              NAVIGATION
              ================================================== */}

          <div>
            <p
              className="
                mb-6
                mt-0
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.22em]
                text-text

                sm:text-[10px]
              "
            >
              {
                labels.navigation
              }
            </p>

            <nav
              className="
                flex
                flex-col
                items-start
                gap-3
              "
              aria-label={t(
                "footer.navigation"
              )}
            >
              {links.map(
                (item) => (
                  <Link
                    key={
                      item.route
                    }
                    to={getRoutePath(
                      item.route,
                      language
                    )}
                    className="
                      text-[9px]
                      font-medium
                      uppercase
                      tracking-[0.18em]
                      text-muted
                      no-underline
                      transition-colors
                      duration-300
                      hover:text-text

                      sm:text-[10px]
                    "
                  >
                    {
                      item.label
                    }
                  </Link>
                )
              )}
            </nav>
          </div>

          {/* ==================================================
              CONTACT
              ================================================== */}

          <div>
            <p
              className="
                mb-6
                mt-0
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.22em]
                text-text

                sm:text-[10px]
              "
            >
              {
                labels.contact
              }
            </p>

            {hasContact && (
              <div
                className="
                  flex
                  flex-col
                  items-start
                  gap-3
                "
              >
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="
                      text-xs
                      leading-6
                      text-muted
                      no-underline
                      transition-colors
                      duration-300
                      hover:text-text
                    "
                  >
                    {
                      email
                    }
                  </a>
                )}

                {phone && (
                  <a
                    href={
                      phoneHref
                    }
                    className="
                      text-xs
                      leading-6
                      text-muted
                      no-underline
                      transition-colors
                      duration-300
                      hover:text-text
                    "
                  >
                    {
                      phone
                    }
                  </a>
                )}
              </div>
            )}

            {!hasContact && (
              <span
                className="
                  text-xs
                  leading-6
                  text-muted
                "
                aria-hidden="true"
              >
                —
              </span>
            )}
          </div>

          {/* ==================================================
              STUDIO + COMPANY
              ================================================== */}

          <div>
            {hasStudio && (
              <div>
                <p
                  className="
                    mb-5
                    mt-0
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.22em]
                    text-text

                    sm:text-[10px]
                  "
                >
                  {
                    labels.studio
                  }
                </p>

                <address
                  className="
                    not-italic
                    text-xs
                    leading-6
                    text-muted
                  "
                >
                  {studioName && (
                    <div>
                      {
                        studioName
                      }
                    </div>
                  )}

                  {studioStreet && (
                    <div>
                      {
                        studioStreet
                      }
                    </div>
                  )}

                  {studioCityLine && (
                    <div>
                      {
                        studioCityLine
                      }
                    </div>
                  )}

                  {studioCountry && (
                    <div>
                      {
                        studioCountry
                      }
                    </div>
                  )}
                </address>

                {studioMapsUrl && (
                  <a
                    href={
                      studioMapsUrl
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="
                      group
                      mt-4
                      inline-flex
                      items-center
                      gap-3
                      border-b
                      border-border
                      pb-1
                      text-[8px]
                      font-semibold
                      uppercase
                      tracking-[0.18em]
                      text-muted
                      no-underline
                      transition-colors
                      duration-300
                      hover:border-text
                      hover:text-text

                      sm:text-[9px]
                    "
                  >
                    {
                      labels.openMap
                    }

                    <span
                      className="
                        transition-transform
                        duration-300
                        group-hover:translate-x-0.5
                      "
                      aria-hidden="true"
                    >
                      ↗
                    </span>
                  </a>
                )}
              </div>
            )}

            {hasCompany && (
              <div
                className={
                  hasStudio
                    ? "mt-8 border-t border-border pt-6"
                    : ""
                }
              >
                <p
                  className="
                    mb-4
                    mt-0
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.22em]
                    text-text

                    sm:text-[10px]
                  "
                >
                  {
                    labels.company
                  }
                </p>

                {legalName && (
                  <p
                    className="
                      m-0
                      text-xs
                      leading-6
                      text-muted
                    "
                  >
                    {
                      legalName
                    }
                  </p>
                )}

                {businessId && (
                  <p
                    className="
                      mb-0
                      mt-1
                      text-[10px]
                      leading-5
                      text-muted
                    "
                  >
                    {
                      labels.businessId
                    }
                    {": "}
                    {
                      businessId
                    }
                  </p>
                )}
              </div>
            )}

            {!hasStudio &&
              !hasCompany && (
                <>
                  <p
                    className="
                      mb-6
                      mt-0
                      text-[9px]
                      font-semibold
                      uppercase
                      tracking-[0.22em]
                      text-text

                      sm:text-[10px]
                    "
                  >
                    {
                      labels.studio
                    }
                  </p>

                  <span
                    className="
                      text-xs
                      leading-6
                      text-muted
                    "
                    aria-hidden="true"
                  >
                    —
                  </span>
                </>
              )}
          </div>
        </div>

        {/* ====================================================
            BOTTOM
            ==================================================== */}

        <div
          className="
            mt-14
            flex
            flex-col
            gap-4
            border-t
            border-border
            pt-6

            sm:mt-16
            sm:flex-row
            sm:items-center
            sm:justify-between
          "
        >
          <p
            className="
              m-0
              text-[9px]
              uppercase
              tracking-[0.18em]
              text-muted

              sm:text-[10px]
            "
          >
            © {year}{" "}
            {
              siteConfig.brand
                .name
            }
          </p>

          <p
            className="
              m-0
              text-[9px]
              uppercase
              tracking-[0.18em]
              text-muted

              sm:text-[10px]
            "
          >
            {
              siteConfig.brand
                .location
            }
          </p>
        </div>
      </div>
    </footer>
  );
}